import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { controlToastState } from "risa-oasis-ui_v2";
import { v4 as uuidv4 } from "uuid";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { medicalPaSubmission } from "../../../api/postCall/medicalPaSubmission";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrderStatus } from "../../../enums/medicalPaOrderStatus";
import {
  resetFileUploadState,
  uploadFileToFirebaseStorage,
} from "../../../redux/slice/fileUploadSlice";
import { AppDispatch } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";
import { mergePdfs } from "../../pharmaQuestionaire/utils/pdfMerger";
import { blobToFile } from "../utils/documentHelper";
import { generatePathForFileUpload } from "../utils/generatePathForFileUpload";

interface UseFormSubmissionProps {
  singleMedicalPaOrder: MedicalPaOrder | null;
  orderId: string | undefined;
  documents: File[];
  downloadURL: string | null;
  progress: number;
}

export const useFormSubmission = ({
  singleMedicalPaOrder,
  orderId,
  documents,
  downloadURL,
  progress,
}: UseFormSubmissionProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const uuid = useMemo(() => uuidv4(), []);

  const handleApiCall = useCallback(async () => {
    const submisionStatus = singleMedicalPaOrder?.status?.submission ?? "";
    if (submisionStatus === MedicalPaOrderStatus.IN_PROGRESS) {
      navigate(`/worklists/`);
    } else if (
      submisionStatus === MedicalPaOrderStatus.COMPLETED ||
      submisionStatus === MedicalPaOrderStatus.ALREADY_FILLED
    ) {
      return;
    } else {
      try {
        await medicalPaSubmission(orderId ?? "");
        controlToastState("form-submission-success");
      } catch (error) {
        controlToastState("form-submission-failure");
        console.error(error);
      }
    }
  }, [singleMedicalPaOrder?.status?.submission, orderId, navigate]);

  const updateClinicalDocument = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await FirestoreService.updateDocument(
        "medical_pa_orders",
        singleMedicalPaOrder?.id ?? "",
        {
          "clinical_document.file_path": generatePathForFileUpload(
            singleMedicalPaOrder?.id ?? "",
            uuid,
          ),
        },
      );
      await handleApiCall();
      dispatch(resetFileUploadState());
      navigate(`/worklists/submission-status/${orderId}`);
    } catch (error) {
      logError(error as Error, "Error updating clinical document");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    singleMedicalPaOrder?.id,
    uuid,
    handleApiCall,
    dispatch,
    navigate,
    orderId,
  ]);

  const submitNormalFlow = useCallback(async () => {
    const faxPdfLink = location?.state?.faxPdfLink;
    const filePath = faxPdfLink
      ? faxPdfLink
      : generatePathForFileUpload(singleMedicalPaOrder?.id ?? "", uuid);
    if (documents && documents.length > 0) {
      setIsSubmitting(true);
      const mergedDocInfo = await mergePdfs(documents);
      if (mergedDocInfo) {
        dispatch(
          uploadFileToFirebaseStorage(
            blobToFile(mergedDocInfo.mergedPdf, `${uuid}.pdf`),
            filePath,
          ),
        );
      }
    }
  }, [
    documents,
    dispatch,
    uuid,
    singleMedicalPaOrder?.id,
    location?.state?.faxPdfLink,
  ]);

  const finalButtonText = useMemo(() => {
    if (isSubmitting) {
      return downloadURL
        ? "Submitting..."
        : `Uploading ${Math.round(progress)}%`;
    }

    return "Submit";
  }, [isSubmitting, downloadURL, progress]);

  return {
    isSubmitting,
    setIsSubmitting,
    updateClinicalDocument,
    submitNormalFlow,
    finalButtonText,
  };
};
